import { Component, Optional } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ItemDetail,
  ItemDetailStatusEnum,
  ShipBin,
} from "app/model/entities/entity-model";
import { InventoryDamageDialogComponent } from "app/inventory/inventory-damage-dialog.component";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { BarcodeService } from "app/shared/barcode.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { NavFns } from "app/shared/nav-fns";
import { ShipBinService } from "app/shared/ship-bin.service";
import { UnitOfWork } from "app/shared/unit-of-work";
import { takeUntil } from "rxjs/operators";
import { ProcessManagerComponent } from "./process-manager.component";
import TextAlign from "@tiptap/extension-text-align";

@Component({
  selector: "app-scan-station",
  templateUrl: "./scan-station.component.html",
  standalone: false,
})
export class ScanStationComponent extends DomainBaseComponent {
  pendingGridOptions!: GridOptionsExt;
  showBackordersOnly = true;

  interval: any;
  refreshOptions!: any[];
  refreshSeconds = 0;
  showRefreshIcon = false;

  lastScanItem!: ItemDetail;
  lastScanShipBin!: ShipBin;
  scanErrorMessage = "";

  constructor(
    public override uow: UnitOfWork,
    protected domainService: DomainService,
    private barcodeService: BarcodeService,
    private shipBinService: ShipBinService,
    protected route: ActivatedRoute,
    @Optional() private processManager: ProcessManagerComponent,
  ) {
    super(domainService);

    // Subscribe to scanner results from parent sidebar
    this.processManager?.scannerResult$.pipe(takeUntil(this.onDestroy)).subscribe((result) => {
      this.onScanSuccess(result as ItemDetail);
    });

    this.processManager?.scannerError$.pipe(takeUntil(this.onDestroy)).subscribe((error) => {
      this.scanErrorMessage = error;
    });

    // Show scanner in sidebar when this page is active
    if (this.processManager) {
      setTimeout(() => {
        this.processManager.showScanStationScanner = true;
        this.processManager.showShipFrequenciesButton = true;
        this.processManager.scannerSearchFn = this.searchItemDetail.bind(this);
      });
    }
  }

  override ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.processManager) {
      this.processManager.showScanStationScanner = false;
      this.processManager.showShipFrequenciesButton = false;
    }
    super.ngOnDestroy();
  }

  ngOnInit() {
    super.ngOnInit();
    this.updateFromContext();
  }

  async updateFromContext() {
    this.refreshOptions = [
      { name: "Every 10 Seconds", refreshSeconds: 10 },
      { name: "Every 30 Seconds", refreshSeconds: 30 },
      { name: "Every 60 Seconds", refreshSeconds: 60 },
      { name: "Every 90 Seconds", refreshSeconds: 90 },
      { name: "Every 180 Seconds", refreshSeconds: 180 },
    ];
    this.refreshSeconds = 90;
    this.setupRefresh();

    this.pendingGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPendingGridReady,
      suppressNoRowsOverlay: true,
      rowModelType: "serverSide",
    });
    AgFns.captureGridRouteParams(this.pendingGridOptions, this.route);

    this.isPageReady = true;
  }

  setupRefresh() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.refreshSeconds === 0) return;
    this.interval = setInterval(() => {
      if (this.isPageReady) {
        this.showRefreshIcon = true;
        this.pendingGridOptions.apiX?.refreshServerSide();
      }
    }, this.refreshSeconds * 1000);
  }

  onPendingGridReady = async (e: any) => {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.markAsScanned.bind(this), {
          label: "Mark as Scanned",
          canDisplay: this.canMarkAsScanned.bind(this),
        }),
        colId: "markAsScanned",
        ...AgFns.fixWidth(140),
      },
      { headerName: "Item ID", field: "id", ...AgFns.fixWidth(140) },
      {
        headerName: "J/O #",
        ...NavFns.createIdCellClickedNavProps("joDetail.joHeader.id", this.router, "/in-process/manage/job-orders"),
        filter: "agNumberColumnFilter",
        type: "leftAligned",
        ...AgFns.fixWidth(150),
      },
      { headerName: "Exp. Ship Date", field: "joDetail.joHeader.origExpectedShipDate", ...AgFns.fixWidth(140) },
      { headerName: "Account", field: "joDetail.joHeader.account.accountName", filter: "agTextColumnFilter", ...AgFns.flexWidth(180) },
      { headerName: "Manufacturer", field: "product.productType.manufacturer.name", filter: "agTextColumnFilter", ...AgFns.flexWidth(150) },
      { headerName: "Description", field: "product.productType.description", filter: "agTextColumnFilter", ...AgFns.flexWidth(300) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(200) },
      {
        ...AgFns.createButtonProps("", this.markDamaged.bind(this), {
          label: "Mark Damaged",
          canDisplay: this.canMarkDamaged.bind(this),
        }),
        colId: "markDamaged",
        ...AgFns.fixWidth(120),
      },
    ];
    AgFns.initGrid(e, colDefs);
    this.updateDatasource();
  };

  updateDatasource() {
    const gridApi = this.pendingGridOptions.apiX;
    if (gridApi == null) return;

    const ds = AgFns.buildDatasource(
      () => this.dbQueryService.createScanStationQuery(this.showBackordersOnly),
      [{ colId: "id", sort: "asc" }],
      { returnFn: () => { this.showRefreshIcon = false; } }
    );
    gridApi.setGridOption("serverSideDatasource", ds);
  }

  onFilterChanged() {
    this.updateDatasource();
  }

  /** Called by the sidebar scanner's searchFn — looks up item by server query. */
  async searchItemDetail(scannedValue: string): Promise<ItemDetail | null> {
    this.scanErrorMessage = "";

    const itd = await this.dbQueryService.getItemDetailByIdForScanStation(scannedValue);

    if (!itd) {
      return null;
    }

    // Already scanned — offer to reprint the ship label
    if (itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessShipPrep) {
      const reprint = await this.dialogService.yesNoDialog(
        "Already Scanned",
        "This item has already been scanned into Ship Prep. Reprint the ship label?",
      );
      if (reprint && itd.joDetail?.joHeader && itd.shipBin) {
        await this.barcodeService.printShipPrepLabel(itd, itd.shipBin);
        this.toastr.success("Label reprinted");
      }
      throw new Error(""); // Already handled — suppress scanner error display
    }

    if (itd.itemDetailStatusId !== ItemDetailStatusEnum.InProcessPendingShipPrep) {
      throw new Error(`Item is in '${itd.itemDetailStatus?.name || itd.itemDetailStatusId}' state`);
    }

    if (!itd.joDetail?.joHeader) {
      throw new Error("Item has no associated Job Order");
    }

    return itd;
  }

  canMarkDamaged(itd: ItemDetail) {
    return itd.itemDetailStatusId < ItemDetailStatusEnum.InProcessVoucher;
  }

  async markDamaged(itd: ItemDetail) {
    const r = await DialogBaseComponent.open(InventoryDamageDialogComponent, this, {
      itemDetail: itd,
    });
    if (r) {
      this.pendingGridOptions.apiX?.refreshServerSide();
    }
  }

  canMarkAsScanned(itd: ItemDetail) {
    return itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingShipPrep;
  }

  async markAsScanned(itd: ItemDetail) {
    if (itd?.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingShipPrep) {
      await this.onScanSuccess(itd);
    }
  }

  /** Handle a successful scan — assign to bin, update status, print labels. */
  async onScanSuccess(itd: ItemDetail) {
    this.scanErrorMessage = "";
    const joh = itd.joDetail!.joHeader;

    // Find or create the ship bin for this item's shipping destination
    const shipBin = await this.shipBinService.findOrCreateShipBin(joh);
    const isNewBin = shipBin.entityAspect.entityState.isAdded();

    // Update item status and assign to ship bin
    itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessShipPrep;
    itd.shipBinId = shipBin.id;
    itd.shipBin = shipBin;

    const entitiesToSave = isNewBin ? [itd, shipBin] : [itd];
    await this.dbSaveService.saveSelectedChanges(entitiesToSave);

    // Print ship-prep label
    await this.barcodeService.printShipPrepLabel(itd, shipBin);

    // Print ship bin label if this is a new bin
    if (isNewBin) {
      await this.barcodeService.printShipBinBarcode(shipBin, joh);
    }

    // Update UI
    this.lastScanItem = itd;
    this.lastScanShipBin = shipBin;

    // Refresh grid from server
    this.pendingGridOptions.apiX?.refreshServerSide();

    this.toastr.success(`Scanned into Bin ${shipBin.id}`);
  }
}
